import { createContext, useContext, useEffect, useState } from "react";
import { RegionResolution, regionName } from "lib/region";
import { APIClient, useAPIContext } from "./APIContext";
import { useAppInsights } from "./AppInsightsContext";

/**
 * The region serving this app, from `GET /api/v1/config`.
 *
 * The app asks rather than deriving a region from its own hostname, which would be wrong on
 * `derma.cur8.co`. See docs/architecture/2026-09-03-Withholding-Out-Of-Region-Members.md.
 */

/** One initial attempt plus this many retries, then the region stays unavailable. */
const RETRIES = 2;
const RETRY_DELAY_MS = 500;
const TIMEOUT_MS = 5000;

const Context = createContext<RegionResolution>({ status: "resolving" });

interface RegionContextProps {
  children: React.ReactNode;
}

async function fetchServingRegion(client: APIClient) {
  const request = client.config.fetchConfig();

  // The client enforces no deadline of its own, and without one a request the clinic's
  // network never answers holds every member page on its loading state.
  const timeout = setTimeout(() => request.abandon(), TIMEOUT_MS);

  try {
    const config = await request.result;
    return config.region || undefined;
  } finally {
    clearTimeout(timeout);
  }
}

export default function RegionContext({ children }: RegionContextProps) {
  const { client } = useAPIContext();
  const appInsights = useAppInsights();

  const [resolution, setResolution] = useState<RegionResolution>({
    status: "resolving",
  });

  useEffect(() => {
    // No client means no way to ask, which is the same answer as a request that failed.
    // Holding at "resolving" instead would leave every member page on its throbber.
    if (!client) {
      setResolution({ status: "unavailable" });
      return;
    }

    let current = true;

    async function resolve(client: APIClient) {
      let lastError: unknown;

      // The client retries a 5xx and does not retry a rejected fetch, so this loop covers
      // the failure it was written for: a single network blip on a clinic phone, which
      // would otherwise leave the guardrail off for the whole session.
      for (let attempt = 0; attempt <= RETRIES; attempt++) {
        if (attempt > 0) {
          await new Promise((resume) =>
            setTimeout(resume, RETRY_DELAY_MS * attempt)
          );
        }

        if (!current) {
          return;
        }

        try {
          const region = await fetchServingRegion(client);

          if (!current) {
            return;
          }

          if (region) {
            setResolution({ status: "resolved", servingRegion: region });
            return;
          }

          // A 200 that omits the field is not a failure and raises no exception, so it needs
          // its own signal: it is how a deployment predating the field looks, and how a
          // partial rollout looks.
          appInsights.trackEvent(
            { name: "ServingRegionUnavailable" },
            { reason: "absent" }
          );
          setResolution({ status: "unavailable" });
          return;
        } catch (error) {
          lastError = error;
        }
      }

      if (!current) {
        return;
      }

      console.error("Could not resolve the serving region:", lastError);
      appInsights.trackException({ exception: lastError as Error });
      appInsights.trackEvent(
        { name: "ServingRegionUnavailable" },
        { reason: "requestFailed" }
      );
      // No clinician-facing message: an unknown region withholds nothing, so there is
      // nothing for them to do about it. The guardrail being off is an operations problem.
      setResolution({ status: "unavailable" });
    }

    resolve(client);

    return () => {
      current = false;
    };
  }, [client, appInsights]);

  // The tab title carries the region too, so it stays legible when the app is one of
  // several tabs open on a phone and nothing else on screen is visible.
  useEffect(() => {
    document.title =
      resolution.status === "resolved"
        ? `Neko Derma ${regionName(resolution.servingRegion)}`
        : "Neko Derma";
  }, [resolution]);

  return <Context.Provider value={resolution}>{children}</Context.Provider>;
}

export function useRegion() {
  return useContext(Context);
}
